import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router, RouterModule } from '@angular/router';
import { map, Observable, of, startWith, take } from 'rxjs';
import { AddFolderDialogComponent } from 'src/app/pages/connectors/details-connector/add-folder-dialog/add-folder-dialog.component';
import { AccountingFoldersModule } from '~modules/accounting-folders/accounting-folders.module';
import { AccountingFolderEntity } from '~modules/accounting-folders/entities/accounting-folder.entity';
import { AccountingFoldersService } from '~modules/accounting-folders/services/accounting-folders.service';

@Component({
  standalone: true,
  selector: 'app-select-or-create-folder-dialog',
  templateUrl: './select-or-create-folder-dialog.component.html',
  styleUrls: ['./select-or-create-folder-dialog.component.scss'],
  imports: [
    CommonModule,
    MatInputModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatIconModule,
    MatAutocompleteModule,
    RouterModule,
    AccountingFoldersModule,
    MatButtonModule
  ]
})
export class SelectOrCreateFolderDialogComponent implements OnInit {

  form: FormGroup;
  folders$: Observable<AccountingFolderEntity[]>;
  allFolders: AccountingFolderEntity[] = [];
  filteredOptions: Observable<AccountingFolderEntity[]> = of([]);

  constructor(
    public dialogRef: MatDialogRef<SelectOrCreateFolderDialogComponent>,
    public createFolderDialogRef: MatDialogRef<AddFolderDialogComponent>,
    private readonly router: Router,
    private readonly folderService: AccountingFoldersService,
    private readonly fb: FormBuilder
  ) {
    this.folders$ = this.folderService.getAll$();
    this.form = this.fb.group({
      folder: [null, Validators.required]
    })
  }

  ngOnInit(): void {
    this.folders$.pipe(take(1)).subscribe(folders => {
      this.allFolders = folders;

      this.filteredOptions = this.form.get('folder')?.valueChanges.pipe(
        startWith(''),
        map(value => {
          const input = typeof value === 'string' ? value : value?.label;
          // custom length validator
          const inputLength = typeof value === 'string' ? value.length : value?.label.length;
          if (inputLength > 128) {
            this.form.get('folder')?.setErrors(Validators.maxLength);
          } else if (inputLength < 1) {
            this.form.get('folder')?.setErrors(Validators.minLength);
          } else {
            this.form.get('folder')?.setErrors(null);
          }
          return this._filter(input || '', this.allFolders);
        })
      ) ?? of([]);
    });

  }

  selectOrAddFolderAndGoToAddConnectorPage(): void {
    const selectedFolder = this.form.get('folder')?.value;
    if (selectedFolder === null) {
      return;
    }
    if (typeof selectedFolder === 'string') {
      if (this.form.valid) {
        this.folderService.create$({
            state: null,
            label: selectedFolder,
            siren: null,
            externalId: null,
            businessStructure: null,
            closingDate: null,
            accountingTool: null
          }).pipe(take(1)).subscribe((createdFolder) => {
              this.dialogRef.close();
              this.router.navigate([`app/folders/${createdFolder.id}/connectors/create`]);
          });
      }
    } else {
      this.dialogRef.close();
      this.router.navigate([`app/folders/${selectedFolder.id}/connectors/create`]);
    }
  }

  private _filter(value: string, folders: AccountingFolderEntity[]): AccountingFolderEntity[] {
    const filterValue = value.toLowerCase();
    return folders.filter(folder => folder.label.toLowerCase().includes(filterValue));
  }

  autocompleteDisplayFunction = (folder?: AccountingFolderEntity): string => {
  return folder ? folder.label : '';
  };


}
